import React, { useState, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Container,
  Grid,
  FormControl,
  Typography,
  TextField,
  Paper,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers-pro";
import CompleteIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDrop from "../../shared/fileDrop";
import { ModalityWorklistFields } from "../../shared/ModalityWorklist";
import { NewStudy as NewStudyAPI, GetPatientStudies, BookSlot, GetNextSlotDate, BookModalitySlot, GetModalitySchedule } from "../../../../../api";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import SlotPicker from "../../../Schedule/SlotPicker";
import { MODALITY_OPTIONS } from "../../../../../constants/Consult";

import {
  GENDER_OPTIONS,
  SPECIES_OPTIONS,
} from "../../../../../constants/Consult";
import moment from "moment";
import dayjs from 'dayjs';


const StudyNew = ({ access, mobile, imaging }) => {
  const initDate = new Date()
  const [study, setStudy] = useState({
    MainDicomTags: {
      StudyDate: moment(initDate).format("YYYYMMDD"),
      StudyTime: moment(initDate).format("HHmm00.000")
    },
    PatientMainDicomTags: {},
    MWL: {
      ScheduledStartDate: moment(initDate).format("YYYYMMDD"),
      ScheduledStartTime: moment(initDate).format("HHmm00.000"),
      AccessionNumber: dayjs().format("YYYYMMDDHHmmss"),
      ScheduledProcedureStepID: dayjs().format("YYYYMMDDHHmmss")
    }
  });
  const [media, setMedia] = useState([]);
  const [modalityWorklist, setModalityWorklist] = useState(false);
  const [ageYears, setAgeYears] = useState('')
  const [ageMonths, setAgeMonths] = useState('')
  const [ageDays, setAgeDays] = useState('')
  const [patientMatches, setPatientMatches] = useState([])
  const [patientDialogOpen, setPatientDialogOpen] = useState(false)
  const [patientLoading, setPatientLoading] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [slotDate, setSlotDate] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  // Modality scheduler state
  const [modDate, setModDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [modality, setModality] = useState('')
  const [modStartTime, setModStartTime] = useState('09:00')
  const [modEndTime, setModEndTime] = useState('09:30')
  const [modCapacity, setModCapacity] = useState(null)
  const [modCapLoading, setModCapLoading] = useState(false)
  const navigate = useNavigate();

  const isParent = !!access.org.settings?.scheduler

  React.useEffect(() => {
    if (!mobile) return
    const childOrg = !isParent ? access.org.id : undefined
    GetNextSlotDate(access.org.id, childOrg).then(r => {
      if (r?.date) setSlotDate(r.date)
      else setSlotDate(dayjs().format('YYYY-MM-DD'))
    }).catch(() => setSlotDate(dayjs().format('YYYY-MM-DD')))
  }, [access.org.id, isParent, mobile])

  const updateValues = useCallback((value, name) => {
    setStudy((prev) => {
      let p = { ...prev },
        obj = name.split(".");
      if (obj.length > 1)
        if (obj[0] in p) p[obj[0]] = { ...p[obj[0]], [obj[1]]: value };
        else p[obj[0]] = { [obj[1]]: value };
      else p[obj[0]] = value;
      return p;
    });
  }, [])

  const updateAgeFields = useCallback((birthDate) => {
    if (birthDate) {
      const duration = moment.duration(moment().diff(birthDate))
      setAgeYears(duration.years())
      setAgeMonths(duration.months())
      setAgeDays(duration.days())
    } else {
      setAgeYears('')
      setAgeMonths('')
      setAgeDays('')
    }
  }, [])

  const updateBirthDateFromAge = useCallback((years, months, days) => {
    const y = parseInt(years) || 0
    const m = parseInt(months) || 0
    const d = parseInt(days) || 0
    const birthDate = moment().subtract(y, 'years').subtract(m, 'months').subtract(d, 'days').format('YYYYMMDD')
    setStudy((prev) => {
      let p = { ...prev }
      if ('PatientMainDicomTags' in p) p.PatientMainDicomTags = { ...p.PatientMainDicomTags, PatientBirthDate: birthDate }
      else p.PatientMainDicomTags = { PatientBirthDate: birthDate }
      return p
    })
  }, [])

  const slotMwlRef = React.useRef(null)

  // Fetch modality capacity when date/modality changes
  React.useEffect(() => {
    if (!imaging || !modDate || !modality) { setModCapacity(null); return }
    setModCapLoading(true)
    GetModalitySchedule(access.org.id, modDate)
      .then(r => {
        const count = r.countByModality?.[modality] || 0
        const limit = r.limits?.[modality]
        setModCapacity({ count, max: limit?.max ?? null })
      })
      .catch(() => setModCapacity(null))
      .finally(() => setModCapLoading(false))
  }, [imaging, modDate, modality, access.org.id])

  const handleMWLChange = useCallback((mwlData) => {
    setStudy(prev => ({ ...prev, MWL: { ...prev.MWL, ...mwlData } }));
  }, []);

  const handleSlotSelect = useCallback((slot) => {
    setSelectedSlot(slot)
    if (slot && slotDate) {
      const mwlUpdate = {
        ScheduledStartDate: moment(slotDate).format('YYYYMMDD'),
        ScheduledStartTime: slot.startTime.replace(':', '') + '00.000'
      }
      slotMwlRef.current = mwlUpdate
      setStudy(prev => ({
        ...prev,
        MWL: { ...prev.MWL, ...mwlUpdate }
      }))
    }
  }, [slotDate])

  let completeRequired =
    study.MainDicomTags.StudyDate &&
    study.MainDicomTags.StudyDescription &&
    study.PatientMainDicomTags.ResponsiblePerson &&
    study.PatientMainDicomTags.PatientName &&
    study.PatientMainDicomTags.PatientID &&
    study.PatientMainDicomTags.PatientSex &&
    study.PatientMainDicomTags.PatientBirthDate &&
    study.PatientMainDicomTags.PatientSpeciesDescription &&
    study.PatientMainDicomTags.PatientBreedDescription &&
    study.PatientMainDicomTags.PatientWeight

  if (modalityWorklist)
    completeRequired = completeRequired &&
      //study.MWL.RequestingPhysician &&
      study.MWL.ScheduledStartDate &&
      study.MWL.ScheduledStartTime &&
      study.MWL.Modality &&
      study.MWL.ProcedureDescription


  const goBack = useCallback(num => {
    if (window.history.state && window.history.state.idx > 0) navigate(num)
    else navigate('/', { replace: true })
  }, [navigate])

  const bookAfterCreate = useCallback((studyId) => {
    if (selectedSlot && studyId) {
      return BookSlot({
        date: slotDate,
        templateId: selectedSlot.templateId,
        slotId: selectedSlot.slotId,
        caseId: studyId
      }).catch(() => {})
    }
  }, [selectedSlot, slotDate])

  const modalityBookAfterCreate = useCallback((studyId) => {
    if (imaging && modality && modDate && studyId) {
      return BookModalitySlot(access.org.id, {
        studyId,
        modality,
        date: modDate,
        startTime: modStartTime,
        endTime: modEndTime
      }).catch(() => {})
    }
  }, [imaging, modality, modDate, modStartTime, modEndTime, access.org.id])

  const handleSubmit = (e) => {
    e.preventDefault();
    const studyData = { ...study };
    if (!modalityWorklist)
      delete studyData.MWL;
    else
      studyData.MWL.aetitle = Array.isArray(access.org.aetitle) ? access.org.aetitle[0] : access.org.aetitle
    if (selectedSlot) studyData.scheduleSlot = { date: slotDate, templateId: selectedSlot.templateId, slotId: selectedSlot.slotId }
    if (mobile) studyData.mobile = true
    if (imaging) studyData.imaging = true
    NewStudyAPI({ ...studyData, org: access.org.id, media }).then(r => {
      bookAfterCreate(r?.id)
      modalityBookAfterCreate(r?.id)
      goBack(-1)
    }).catch(e => e)
  }

  const consultStudy = async () => {
    const studyData = { ...study };
    if (!modalityWorklist)
      delete studyData.MWL;
    else
      studyData.MWL.aetitle = Array.isArray(access.org.aetitle) ? access.org.aetitle[0] : access.org.aetitle
    if (selectedSlot) studyData.scheduleSlot = { date: slotDate, templateId: selectedSlot.templateId, slotId: selectedSlot.slotId }
    if (mobile) studyData.mobile = true
    if (imaging) studyData.imaging = true
    NewStudyAPI({ ...studyData, org: access.org.id, media }).then(r => {
      bookAfterCreate(r?.id)
      modalityBookAfterCreate(r?.id)
      navigate(`/studies/${r.id}/consult`, { replace: true })
    }).catch(e => e)
  }

  const updateStudy = (e) => {
    setStudy((prev) => {
      let p = { ...prev },
        obj = e.target.name.split(".");
      if (obj.length > 1)
        if (obj[0] in p) p[obj[0]] = { ...p[obj[0]], [obj[1]]: e.target.value };
        else p[obj[0]] = { [obj[1]]: e.target.value };
      else p[obj[0]] = e.target.value;
      return p;
    });
  };

  const handlePatientIdBlur = useCallback(() => {
    const pid = study.PatientMainDicomTags.PatientID?.trim()
    if (!pid) return
    setPatientLoading(true)
    GetPatientStudies(access.org.id, pid).then(studies => {
      if (!studies?.length) return
      const seen = new Set()
      const unique = studies.filter(s => {
        const name = s.PatientMainDicomTags?.PatientName || ''
        if (seen.has(name)) return false
        seen.add(name)
        return true
      })
      if (unique.length) {
        setPatientMatches(unique)
        setPatientDialogOpen(true)
      }
    }).catch(() => {}).finally(() => setPatientLoading(false))
  }, [study.PatientMainDicomTags.PatientID, access.org.id])

  const loadPatientData = useCallback((s) => {
    const tags = s.PatientMainDicomTags || {}
    setStudy(prev => ({
      ...prev,
      PatientMainDicomTags: { ...prev.PatientMainDicomTags, ...tags }
    }))
    if (tags.PatientBirthDate) updateAgeFields(tags.PatientBirthDate)
    setPatientDialogOpen(false)
  }, [updateAgeFields])
  
  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Typography component="h2" variant="h6" gutterBottom>
          New Study:
        </Typography>
        <Paper variant="outlined" sx={{ p: 1, mb: 3, width: 600 }}>
          <Grid
            sx={{ mt: 1, mb: 1 }}
            container
            spacing={3}
          >
            <FormControl fullWidth variant="standard">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  name="MainDicomTags.StudyDate"
                  label="Study Date/Time"
                  required
                  defaultValue={dayjs(initDate)}
                  slotProps={{
                    textField: {
                      required: true,
                    },
                  }}
                  onChange={newValue => {
                    updateValues(
                      (newValue ? moment(newValue.$d).format("YYYYMMDD") : ''),
                      "MainDicomTags.StudyDate"
                    )
                    updateValues(
                      (newValue ? moment(newValue.$d).format("HHmm00.000") : ''),
                      "MainDicomTags.StudyTime"
                    )
                  }}
                  textField={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl fullWidth variant="standard">
              <TextField
                name="MainDicomTags.StudyDescription"
                label="Study Description (ie. Thorax XR, Head CT, Abdominal US)"
                value={study.MainDicomTags.StudyDescription || ""}
                onChange={updateStudy}
                required
              />
            </FormControl>
            <FormControl fullWidth variant="standard">
              <TextField
                name="PatientMainDicomTags.PatientID"
                label="Patient ID"
                value={study.PatientMainDicomTags.PatientID || ''}
                onChange={updateStudy}
                onBlur={handlePatientIdBlur}
                required
                slotProps={{
                  input: {
                    endAdornment: patientLoading ? <CircularProgress size={20} /> : null
                  }
                }}
              />
            </FormControl>
            <FormControl fullWidth variant="standard">
              <TextField
                name="PatientMainDicomTags.ResponsiblePerson"
                label="Owner Name"
                required
                value={study.PatientMainDicomTags.ResponsiblePerson || ''}
                onChange={updateStudy}
              />
            </FormControl>
            <FormControl fullWidth variant="standard">
              <TextField
                name="PatientMainDicomTags.PatientName"
                label="Patient Name"
                value={study.PatientMainDicomTags.PatientName || ''}
                onChange={updateStudy}
                required
              />
            </FormControl>
            <FormControl fullWidth variant="standard">
              <TextField
                id="gender"
                value={study.PatientMainDicomTags.PatientSex || ''}
                name="PatientMainDicomTags.PatientSex"
                label="Gender"
                variant="outlined"
                onChange={updateStudy}
                select
                required
              >
                {GENDER_OPTIONS.map((gender) => (
                  <MenuItem key={gender.key} value={gender.val}>
                    {gender.key}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <Grid container size={12} spacing={1}>
              <FormControl fullWidth variant="standard">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    name="PatientMainDicomTags.PatientBirthDate"
                    label="Birthdate/Age"
                    required
                    slotProps={{
                      textField: {
                        required: true,
                      },
                    }}
                    value={study.PatientMainDicomTags.PatientBirthDate ? dayjs(study.PatientMainDicomTags.PatientBirthDate) : null}
                    onChange={(newValue) => {
                      const formattedDate = newValue ? moment(newValue.$d).format("YYYYMMDD") : ''
                      updateValues(formattedDate, "PatientMainDicomTags.PatientBirthDate")
                      updateAgeFields(formattedDate)
                    }}
                    textField={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </FormControl>
              <Grid size={4}>
                <TextField
                  value={ageYears}
                  onChange={e => {
                    setAgeYears(e.target.value)
                    updateBirthDateFromAge(e.target.value, ageMonths, ageDays)
                  }}
                  helperText="Years"
                  inputProps={{ type: 'number', min: 0 }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  value={ageMonths}
                  onChange={e => {
                    setAgeMonths(e.target.value)
                    updateBirthDateFromAge(ageYears, e.target.value, ageDays)
                  }}
                  helperText="Months"
                  inputProps={{ type: 'number', min: 0 }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  value={ageDays}
                  onChange={e => {
                    setAgeDays(e.target.value)
                    updateBirthDateFromAge(ageYears, ageMonths, e.target.value)
                  }}
                  helperText="Days"
                  inputProps={{ type: 'number', min: 0 }}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth variant="standard">
              <Autocomplete
                id="species"
                freeSolo
                name="PatientMainDicomTags.PatientSpeciesDescription"
                options={SPECIES_OPTIONS}
                fullWidth
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                value={study.PatientMainDicomTags.PatientSpeciesDescription || ''}
                getOptionLabel={(option) => option}
                onChange={(event, value) => {
                  if (value) {
                    updateValues(
                      value,
                      "PatientMainDicomTags.PatientSpeciesDescription"
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField required {...params} label="Species" />
                )}
              />
            </FormControl>
            <FormControl fullWidth variant="standard">
              <TextField
                name="PatientMainDicomTags.PatientBreedDescription"
                label="Breed"
                value={study.PatientMainDicomTags.PatientBreedDescription || ''}
                onChange={updateStudy}
                required
              />
            </FormControl>
            <FormControl fullWidth variant="standard">
              <TextField
                type="number"
                name="PatientMainDicomTags.PatientWeight"
                label="Weight (kgs)"
                required
                value={study.PatientMainDicomTags.PatientWeight || ''}
                onWheel={(e) => e.target.blur()}
                onChange={updateStudy}
              />
            </FormControl>
          </Grid>
        </Paper>
        {(mobile || imaging) && (
          <Accordion sx={{ p: 1, mb: 4, width: 600 }} expanded={scheduleOpen} onChange={(_, isExpanded) => setScheduleOpen(isExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="schedule-content" id="schedule-header">
              <Typography variant="subtitle1" fontWeight="bold">Schedule</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {mobile && (
                <>
                  <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Select Date"
                        value={slotDate ? dayjs(slotDate) : null}
                        onChange={v => { if (v) { setSlotDate(v.format('YYYY-MM-DD')); setSelectedSlot(null) } }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                  {slotDate && (
                    <SlotPicker
                      orgId={access.org.id}
                      date={slotDate}
                      selectedSlot={selectedSlot}
                      onSelect={handleSlotSelect}
                      filterChildOrg={!isParent ? access.org.id : undefined}
                      isParent={isParent}
                    />
                  )}
                </>
              )}
              {imaging && (
                <>
                  <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Select Date"
                        value={modDate ? dayjs(modDate) : null}
                        onChange={v => { if (v) setModDate(v.format('YYYY-MM-DD')) }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                  <TextField select label="Modality" value={modality} onChange={e => {
                    setModality(e.target.value)
                    if (modalityWorklist) handleMWLChange({ Modality: e.target.value })
                  }} fullWidth sx={{ mb: 2 }}>
                    {(access.org.settings?.modalityColumns || MODALITY_OPTIONS).map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </TextField>
                  {modality && modDate && (
                    <>
                      {modCapLoading
                        ? <CircularProgress size={16} sx={{ mb: 1 }} />
                        : modCapacity && (
                          <Chip size="small" sx={{ mb: 2 }}
                            label={`${modality}: ${modCapacity.count}/${modCapacity.max ?? '∞'} scheduled`}
                            color={modCapacity.max != null && modCapacity.count >= modCapacity.max ? 'error' : 'default'}
                          />
                        )
                      }
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TimePicker label="Start Time"
                            value={dayjs(`2000-01-01T${modStartTime}`)}
                            onChange={v => {
                              if (!v) return
                              const s = v.format('HH:mm')
                              setModStartTime(s)
                              const endMins = v.hour() * 60 + v.minute() + 30
                              setModEndTime(`${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`)
                            }}
                            sx={{ flex: 1 }}
                          />
                          <TimePicker label="End Time"
                            value={dayjs(`2000-01-01T${modEndTime}`)}
                            onChange={v => v && setModEndTime(v.format('HH:mm'))}
                            sx={{ flex: 1 }}
                          />
                        </Box>

                      </LocalizationProvider>
                    </>
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        )}
        {access.org.settings?.modalityWorklist && (
          <Accordion sx={{ p: 1, mb: 4, width: 600 }} expanded={modalityWorklist} onChange={(_, isExpanded) => setModalityWorklist(isExpanded)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="mwl-content"
              id="mwl-header"
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Modality Worklist
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ModalityWorklistFields 
                key={selectedSlot?.slotId || 'mwl'}
                required={modalityWorklist}
                onMWLChange={handleMWLChange}
                initialData={{ ...(slotMwlRef.current || {}), ...(modality ? { Modality: modality } : {}) }}
                modalityOptions={access.org.settings?.modalityColumns}
              />
            </AccordionDetails>
          </Accordion>
        )}
        <FileDrop media={media} setMedia={setMedia} />
        <Grid container spacing={3} justifyContent="left">
          {!mobile && (<Button startIcon={<CompleteIcon />} variant="outlined" color="primary" type="submit" disabled={!completeRequired}>
            Save
          </Button>)}
          <Button startIcon={<AssignmentIcon />} variant="contained" color="secondary" onClick={() => consultStudy()} disabled={!completeRequired}>
            Consult
          </Button>
        </Grid>
      </form>
      <Dialog open={patientDialogOpen} onClose={() => setPatientDialogOpen(false)}>
        <DialogTitle>Patient Found — Load Information?</DialogTitle>
        <DialogContent>
          <List>
            {patientMatches.map((s, i) => (
              <ListItemButton key={s.id || i} onClick={() => loadPatientData(s)}>
                <ListItemText
                  primary={s.PatientMainDicomTags?.PatientName || 'Unknown'}
                  secondary={`${s.MainDicomTags?.StudyDescription || ''} — ${s.MainDicomTags?.StudyDate || ''}`}
                />
              </ListItemButton>
            ))}
          </List>
          <Button onClick={() => setPatientDialogOpen(false)}>Cancel</Button>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default StudyNew